<ng-container *ngIf="
    type !== 'label' &&
    type !== 'textarea' &&
    type !== 'select' &&
    type !== 'ejs-dropdown' &&
    type !== 'checkbox' &&
    type !== 'radio' &&
    type !== 'slide-toggle' &&
    type !== 'hidden' &&
    type !== 'radio-group' &&
    type !== 'autocomplete' &&
    type !== 'checkbox-list' &&
    type !== 'link'
  ">
    <label class="top-label field-label"
           *ngIf="!hideLabel"
           [id]="labelId"
           [for]="inputId">
        {{ fieldLabel | translate }}<span [class.disabled]="this.formControl?.status === 'DISABLED'"
                                          class="required-label text-danger"
                                          *ngIf="isRequired || this.formControl?.hasValidator(Validators.required)">*</span>
        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
    </label>
    <div>
        <div>
            <mat-form-field class="bottom-gap tl-labelless-field"
                            [formGroup]="formGroup"
                            [class.mat-form-field-invalid]="forceInvalid || showErrorText || (_formStateApplied && !!formControl && formControl.invalid)"
                            [appearance]="appearance">
                <ng-container [ngTemplateOutlet]="preContent"></ng-container>
                <span matPrefix class="prefix" *ngIf="prefix">
                    {{ prefix | translate }}
                </span>
                <input #inputElement
                       matInput
                       class="field-font"
                       type="{{ type }}"
                       [required]="isRequired"
                       [formControlName]="fieldControlName"
                       [name]="fieldControlName"
                       [id]="inputId"
                       (change)="this.valueChanged.emit($event)"
                       (keypress)="onKeyPress($event)"
                       (keyup)="keyup.emit($event)"
                       placeholder="{{ placeholder | translate }}"
                       autocomplete="off"
                       [tabIndex]="tabIndex"
                       [errorStateMatcher]="immediateErrorStateMatcher"
                       />
                <span matSuffix class="suffix">
                    {{ suffix | translate }}
                    <span class="calc-field" *ngIf="calcValue">{{ calcValue }}&nbsp;{{ calcSuffix | translate }}</span>
                </span>
                <ng-container [ngTemplateOutlet]="postContent"></ng-container>
            </mat-form-field>
            <mat-error class="mat-error" *ngIf="showErrorText">{{ errorText }}</mat-error>
        </div>
        <div *ngIf="warningText" class="mat-warning">
            <svg-icon class="warning-icon"
                      src="assets/svg/warning.svg">
            </svg-icon>
            <span class="mat-warning-text">
                {{ warningText }}
            </span>
        </div>

    </div>
</ng-container>

<ng-container *ngIf="type === 'hidden'">
    <div [formGroup]="formGroup" style="visibility: hidden; width: 0">
        <input type="hidden"
               [name]="fieldControlName"
               [formControlName]="fieldControlName"
               [id]="inputId" />
    </div>
</ng-container>

<!-- **** label **** -->
<ng-container *ngIf="type === 'label'">
    <label class="top-label field-label">
        {{ fieldLabel | translate }}
        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
    </label>
    <ng-container [ngTemplateOutlet]="preContent"></ng-container>
    <!-- A field value, not a heading. This was an <h3>, which gave every read-only field on every
         form heading size and weight - and p-2/mb-0, Bootstrap names that now resolve to Tailwind's
         scale since Tailwind loads last. Consumers patched it back with ::ng-deep + !important
         rather than fix it here; those overrides are gone with it. -->
    <div class="field-value"
        [class.has-value-suffix]="labelValueSuffix"
        [class.label-value-in-error]="showLabelValueInError">
        <mat-icon class="alert-icon" *ngIf="showLabelValueInError">error</mat-icon>
        <span class="label-value bottom-gap">{{ labelValue ?? '&nbsp;' }}</span>
        <span class="pill field-value-suffix" *ngIf="labelValueSuffix">{{ labelValueSuffix }}</span>
    </div>
    <ng-container [ngTemplateOutlet]="postContent"></ng-container>
</ng-container>

<!-- **** link **** -->
<ng-container *ngIf="type === 'link'">
    <label class="top-label field-label">
        {{ fieldLabel | translate }}

        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
    </label>
    <ng-container [ngTemplateOutlet]="preContent"></ng-container>
    <!-- Same defect as type="label" above, same fix - they shared the element and the classes, so
         leaving this one an <h3> would have kept every rule below keyed on the tag. -->
    <div class="field-value">
        <a class="link bottom-gap" (click)="this.fieldClick.emit()">
            {{ labelValue }}
        </a>
    </div>
    <ng-container [ngTemplateOutlet]="postContent"></ng-container>
</ng-container>

<!-- **** checkbox **** -->
<ng-container *ngIf="type === 'checkbox'">
    <div fxLayout="row">
        <ng-container [ngTemplateOutlet]="preContent"></ng-container>
        <div [formGroup]="formGroup"
             class="check-box-form-control-wrapper"
             [class.bottom-gap]="!noBottomGap">
            <!-- A SPACER, not a label: the &nbsp; reserves the line every labelled field beside it
                 spends on its caption, so a checkbox in a form grid lines up with them. In a
                 toolbar there is no such line to match and it just pushes the checkbox a
                 line-height below the controls next to it, so `hideLabel` drops it here the same
                 way it drops the real label on a text field. -->
            <label class="top-label" *ngIf="!hideLabel">&nbsp;</label>

            <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
            <mat-checkbox #inputElement
                          ng-class="bottom-gap"
                          [id]="inputId"
                          [formControlName]="fieldControlName"
                          [formGroup]="formGroup"
                          labelPosition="{{ labelPosition }}"
                          (change)="this.valueChanged.emit($event)"
                          [checked]="formState === true"
                          (click)="disabled === true ? $event.preventDefault() : return"
                          [tabIndex]="tabIndex"
                          [indeterminate]="indeterminate">{{ fieldLabel | translate }}</mat-checkbox>
        </div>
        <ng-container [ngTemplateOutlet]="postContent"></ng-container>
    </div>
</ng-container>

<!-- **** radio **** -->
<ng-container *ngIf="type === 'radio'">
    <div fxLayout="row" fxLayout="row">
        <ng-container [ngTemplateOutlet]="preContent"></ng-container>
        <div [formGroup]="formGroup" fxLayout="column" class="bottom-gap">
            <label class="top-label"
                   [id]="labelId"
                   [for]="inputId"
                   (click)="radioButtonField.focus()">
                {{ fieldLabel | translate }}<span [class.disabled]="this?.formControl?.status === 'DISABLED'"
                                                  class="required-label text-danger font-bold"
                                                  *ngIf="this.formControl?.hasValidator(Validators.required)">*</span>
                <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
            </label>

            <mat-radio-group [id]="inputId"
                             [name]="fieldControlName"
                             [formControlName]="fieldControlName"
                             (change)="this.valueChanged.emit($event)"
                             [tabIndex]="tabIndex">
                <mat-radio-button #radioButtonField="matRadioButton"
                                  [ngStyle]="{ 'margin-top': '15px' }"
                                  [checked]="formState === true"></mat-radio-button>
            </mat-radio-group>
        </div>
        <ng-container [ngTemplateOutlet]="postContent"></ng-container>
    </div>
</ng-container>

<!-- **** ejs-dropdown **** -->
<ng-container *ngIf="type === 'ejs-dropdown'" [formGroup]="formGroup">
    <app-dropdown-form-field [formGroup]="formGroup"
                             formControlName="{{ fieldControlName }}"
                             [fieldLabel]="fieldLabel"
                             [options]="options"
                             [disabled]="disabled"
                             [loading]="loading"
                             [placeholder]="placeholder"
                             [debugMode]="debugMode"
                             [allowFiltering]="allowFiltering"
                             [itemTemplate]="itemTemplate"
                             [valueTemplate]="valueTemplate"
                             [enableVirtualization]="enableVirtualization"
                             (selectionChange)="onSelectionChange($event)"
                             [tabIndex]="tabIndex"></app-dropdown-form-field>
</ng-container>

<!-- **** select **** -->
<ng-container *ngIf="type === 'select'" [formGroup]="formGroup">
    <label class="top-label field-label"
           [id]="labelId"
           [for]="inputId"
           (click)="selectField.open()">
        {{ fieldLabel | translate }}<span [class.disabled]="this?.formControl?.status === 'DISABLED'"
                                          class="required-label text-danger font-bold"
                                          *ngIf="isRequired || this.formControl?.hasValidator(Validators.required)">*</span>
        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
    </label>
    <mat-form-field class="bottom-gap tl-labelless-field"
                    [formGroup]="formGroup"
                    [class.isLoading]="!options"
                    [appearance]="appearance">
        <mat-select #selectField="matSelect"
                    [required]="this.formControl?.hasValidator(Validators.required)"
                    [formControlName]="fieldControlName"
                    [id]="inputId"
                    (selectionChange)="onSelectionChange($event)"
                    [placeholder]="placeholder | translate"
                    msInfiniteScroll
                    (infiniteScroll)="getNextBatch()"
                    [complete]="getSelectScrollComplete()"
                    [tabIndex]="tabIndex">
            <mat-option *ngFor="let option of options$ | async"
                        [value]="option.value"
                        [class]="option?.class">
                {{ localizeOptions === true ? (option.text | translate) : option.text }}
            </mat-option>
        </mat-select>
        <mat-spinner diameter="20"></mat-spinner>
    </mat-form-field>
</ng-container>

<!-- **** radio-group **** -->
<ng-container *ngIf="type === 'radio-group'">
    <label class="top-label"
           [id]="labelId"
           [for]="inputId"
           (click)="focusRadioGroup(radioGroupField)">
        {{ fieldLabel | translate }}<span class="text-danger font-bold"
                                          *ngIf="isRequired || this.formControl?.hasValidator(Validators.required)">*</span>
        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
    </label>

    <div [formGroup]="formGroup" fxLayout="row" style="overflow: hidden" class="bottom-gap">
        <ng-container [ngTemplateOutlet]="preContent"></ng-container>
        <div fxLayout="column" style="overflow: hidden !important; width: 100%">
            <mat-radio-group #radioGroupField="matRadioGroup"
                             [id]="inputId"
                             [formControlName]="fieldControlName"
                             [required]="isRequired"
                             (change)="this.valueChanged.emit($event)"
                             [fxLayout]="fieldLayout"
                             [tabIndex]="tabIndex">
                <ng-container *ngFor="let option of options">
                    <mat-radio-button [value]="option.value"
                                      [checked]="option.value === formGroup?.get(fieldControlName)?.value"
                                      matTooltip="{{ option.text | translate }}">
                        {{
                        localizeOptions === true ? (option.text | translate) : option.text
                        }}
                    </mat-radio-button>
                </ng-container>
            </mat-radio-group>
        </div>
        <ng-container [ngTemplateOutlet]="postContent"></ng-container>
    </div>
</ng-container>

<!-- **** slide-toggle **** -->
<ng-container *ngIf="type === 'slide-toggle'">
    <slide-toggle [formGroup]="formGroup" class="bottom-gap">
        <label class="top-label"
               [id]="labelId"
               [for]="inputId"
               (click)="slideToggleField.focus()"></label>
        <div class="slide-label"
             (click)="slideToggleField.focus()">
            {{ fieldLabel | translate }}<span [class.disabled]="this?.formControl?.status === 'DISABLED'"
                                              class="required-label text-danger font-bold"
                                              *ngIf="isRequired || this.formControl?.hasValidator(Validators.required)">*</span>
        </div>
        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
        <mat-slide-toggle #slideToggleField="matSlideToggle"
                          [formGroup]="formGroup"
                          [formControlName]="fieldControlName"
                          [id]="inputId"
                          (change)="this.valueChanged.emit($event)"
                          [disabled]="isDisabled"
                          [labelPosition]="labelPosition"
                          [tabIndex]="tabIndex">
            {{ valueLabel | translate }}
        </mat-slide-toggle>
    </slide-toggle>
</ng-container>

<!-- **** autocomplete component **** -->
<ng-container *ngIf="type === 'autocomplete'">
    <label class="top-label field-label"
           [id]="labelId"
           [for]="inputId">
        {{ fieldLabel | translate }}<span [class.disabled]="formControl?.status === 'DISABLED'"
                                          class="required-label text-danger"
                                          *ngIf="isRequired || this.formControl?.hasValidator(Validators.required)">*</span>
        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
    </label>
    <mat-form-field class="bottom-gap tl-labelless-field" [formGroup]="formGroup">
        <ng-container [ngTemplateOutlet]="preContent"></ng-container>
        <input #searchText
               matInput
               class="field-font"
               type="{{ type }}"
               [required]="isRequired"
               [formControlName]="fieldControlName"
               [name]="fieldControlName"
               [id]="inputId"
               (change)="this.valueChanged.emit($event)"
               [matAutocomplete]="autoCompleteField"
               (keypress)="onKeyPress($event)"
               (keydown)="onKeyDown($event)"
               placeholder="{{ placeholder | translate }}"
               [tabIndex]="tabIndex" />
        <mat-autocomplete autoActiveFirstOption
                          #autoCompleteField="matAutocomplete"
                          (optionSelected)="onAutoCompleteOptionSelected($event)"
                          [displayWith]="displayFn.bind(this)">
            <mat-option *ngFor="let option of options$ | async"
                        [value]="option.value"
                        [class]="option?.class">
                {{ localizeOptions === true ? (option.text | translate) : option.text }}
            </mat-option>
        </mat-autocomplete>
        <ng-container [ngTemplateOutlet]="postContent"></ng-container>
    </mat-form-field>
</ng-container>

<!-- **** checkbox-list **** -->
<ng-container *ngIf="type === 'checkbox-list'">
    <label class="top-label field-label"
           [id]="labelId"
           [for]="inputId"
           (click)="selectionListField.focus()">
        {{ fieldLabel | translate }}<span [class.disabled]="formControl?.status === 'DISABLED'"
                                          class="required-label text-danger"
                                          *ngIf="isRequired || this.formControl?.hasValidator(Validators.required)">*</span>
        <span class="walkme-anchor" [attr.walkme-anchor]="fieldLabel"></span>
    </label>
    <ng-container [ngTemplateOutlet]="preContent"></ng-container>
    <div [formGroup]="formGroup" style="width: 100%; max-width: 100%" class="bottom-gap">
        <mat-selection-list #selectionListField="matSelectionList"
                            [id]="inputId"
                            [formControlName]="fieldControlName"
                            (selectionChange)="this.valueChanged.emit($event)"
                            [tabIndex]="tabIndex">
            <mat-list-option *ngFor="let option of options"
                             [value]="option.value"
                             checkboxPosition="before"
                             [selected]="(formState ?? []).includes(option.value)">
                {{ localizeOptions === true ? (option.text | translate) : option.text }}
            </mat-list-option>
        </mat-selection-list>
    </div>
    <ng-container [ngTemplateOutlet]="postContent"></ng-container>
</ng-container>

<ng-template #preContent>
    <ng-content select="[pre-content]"></ng-content>
</ng-template>

<ng-template #postContent>
    <ng-content select="[post-content]"></ng-content>
</ng-template>
